import { useCallback, useEffect, useState } from "react";
import { useUserInfo } from "GlobalStates/User";
import {
  acceptPolicy,
  fetchLatestAcceptance,
  fetchRequiredPolicies,
} from "./apis/policies";

const buildDeviceMeta = () => {
  const locale = navigator?.language || undefined;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { locale, timezone };
};

export const usePolicyGate = () => {
  const { user } = useUserInfo();
  const [loading, setLoading] = useState(true);
  const [requiredPolicy, setRequiredPolicy] = useState(null);
  const [needsAcceptance, setNeedsAcceptance] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const refresh = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      const { data } = await fetchRequiredPolicies();
      const required = data?.required?.[0] || null;
      if (!required?.policyKey) {
        setRequiredPolicy(null);
        setNeedsAcceptance(false);
        setLoading(false);
        return;
      }
      setRequiredPolicy(required);
      const latest = await fetchLatestAcceptance(required.policyKey);
      const acceptedVersion = latest?.data?.acceptance?.policyVersion;
      setNeedsAcceptance(acceptedVersion !== required.policyVersion);
    } catch (error) {
      setNeedsAcceptance(false);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    if (!user?.token) return;
    refresh();
  }, [user?.token, refresh]);

  const handleAccept = async () => {
    if (!requiredPolicy?.policyKey) return;
    setAccepting(true);
    try {
      const meta = buildDeviceMeta();
      await acceptPolicy({
        policyKey: requiredPolicy.policyKey,
        ...meta,
      });
      setNeedsAcceptance(false);
    } finally {
      setAccepting(false);
    }
  };

  return {
    loading,
    requiredPolicy,
    needsAcceptance,
    accepting,
    refresh,
    handleAccept,
  };
};
